import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { t } from '../i18n';
import { api, qs } from '../lib/api';
import { useDocuments } from '../lib/queries';
import { fmtDate, fmtMoney, fmtMonth } from '../lib/format';
import { Empty, ErrorState, Input, Pagination, SkeletonRows, useDebounced } from '../components/ui';
import { DocTable } from '../components/DocTable';
import { UploadButton } from '../components/UploadDialog';
import { IconSearch, IconUpload, IconX } from '../components/icons';
import { MarginBadge, ProfitKpi, profitTitle, useProfitSummary } from '../components/Margin';
import { useCan } from '../lib/auth';

/** invoices = not cancelled; cancelled = cancelled by a credit note; credits = credit notes not linked to an invoice. */
type Totals = { invoices: number; cancelled: number; credits: number; subtotal: number | null; vat: number | null; total: number | null };
type Period = Totals & { start: string; end: string };
type Sales = {
  group: 'week' | 'month';
  current: {
    week: Totals & { from: string; to: string };
    prevWeek: Totals & { from: string; to: string };
    prevWeekToDate: Totals;
    month: Totals & { from: string; to: string };
    prevMonth: Totals & { from: string; to: string };
    prevMonthToDate: Totals;
    year: Totals & { from: string; to: string };
  };
  periods: Period[];
  filteredTotals: Totals;
};

const INVOICE_TYPES = 'tax_invoice,tax_invoice_receipt,credit_note';

/** % change vs the same number of days in the previous period. */
function Delta({ now, before }: { now: number | null; before: number | null }) {
  if (!before || now == null) return null;
  const pct = Math.round(((now - before) / Math.abs(before)) * 100);
  if (!Number.isFinite(pct)) return null;
  const up = pct >= 0;
  return (
    <span style={{ color: up ? 'var(--ok)' : 'var(--bad)' }} title="בהשוואה לאותה נקודת זמן בתקופה הקודמת">
      <bdi dir="ltr">{up ? '▲' : '▼'} {Math.abs(pct)}%</bdi>
    </span>
  );
}

export function InvoicesPage() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get('q') ?? '');
  const dq = useDebounced(q.trim(), 300);
  const group = (params.get('group') as 'week' | 'month') || 'month';
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';
  const sort = (params.get('sort') as 'issueDate' | 'number' | 'total' | 'dueDate' | 'importedAt') || 'issueDate';
  const dir = (params.get('dir') as 'asc' | 'desc') || 'desc';
  const page = Number(params.get('page')) || 1;

  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(params);
    for (const [k, v] of Object.entries(patch)) (v ? next.set(k, v) : next.delete(k));
    if (!('page' in patch)) next.delete('page');
    setParams(next, { replace: true });
  };
  useEffect(() => {
    if ((params.get('q') ?? '') !== dq) update({ q: dq || null });
  }, [dq]);

  const sales = useQuery({
    queryKey: ['sales', group, from, to, dq],
    queryFn: () => api<Sales>(`/api/sales${qs({ group, from, to, q: dq })}`),
    placeholderData: keepPreviousData,
  });
  const can = useCan();
  const showProfit = can('profit.view');
  const profit = useProfitSummary({ from, to });
  const profitByMonth = new Map((profit.data?.months ?? []).map((m) => [m.month, m]));
  const docs = useDocuments({ types: INVOICE_TYPES, q: dq || undefined, from: from || undefined, to: to || undefined, sort, dir, page, pageSize: 50 });

  const c = sales.data?.current;
  const setRange = (f: string | null, tt: string | null) => update({ from: f, to: tt });
  const rangeActive = (f: string, tt: string) => from === f && to === tt;
  const maxPeriod = Math.max(1, ...(sales.data?.periods.map((p) => Math.abs(p.subtotal ?? 0)) ?? [1]));

  const periodLabel = (p: Period) =>
    group === 'month' ? fmtMonth(p.start.slice(0, 7)) : `${fmtDate(p.start).slice(0, 5)} – ${fmtDate(p.end)}`;

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{t.nav.invoices}</h1>
          <div className="sub">כל חשבונית שמופקת במורנינג נכנסת לכאן אוטומטית. חשבונית שבוטלה בחשבונית זיכוי אינה נספרת במכירות.</div>
        </div>
        <UploadButton>
          <IconUpload /> {t.profile.upload}
        </UploadButton>
      </div>

      <div className="stack">
        {/* Headline sales */}
        <section className="kpis" aria-label="מכירות">
          <SalesKpi label="נמכר השבוע" t={c?.week} compare={c?.prevWeekToDate} loading={sales.isLoading} onClick={() => c && setRange(c.week.from, c.week.to)} active={Boolean(c && rangeActive(c.week.from, c.week.to))} />
          <SalesKpi label="שבוע שעבר" t={c?.prevWeek} loading={sales.isLoading} onClick={() => c && setRange(c.prevWeek.from, c.prevWeek.to)} active={Boolean(c && rangeActive(c.prevWeek.from, c.prevWeek.to))} />
          <SalesKpi label="נמכר החודש" t={c?.month} compare={c?.prevMonthToDate} loading={sales.isLoading} onClick={() => c && setRange(c.month.from, c.month.to)} active={Boolean(c && rangeActive(c.month.from, c.month.to))} />
          <SalesKpi label="חודש שעבר" t={c?.prevMonth} loading={sales.isLoading} onClick={() => c && setRange(c.prevMonth.from, c.prevMonth.to)} active={Boolean(c && rangeActive(c.prevMonth.from, c.prevMonth.to))} />
          <SalesKpi label={`נמכר השנה`} t={c?.year} loading={sales.isLoading} onClick={() => c && setRange(c.year.from, c.year.to)} active={Boolean(c && rangeActive(c.year.from, c.year.to))} />
        </section>

        {/* Gross margin — always in sight (profit permission only) */}
        {showProfit ? (
          <section className="kpis" aria-label="רווחיות">
            <ProfitKpi label="רווחיות — כל החשבוניות" p={profit.data?.all} />
            <ProfitKpi label="רווחיות החודש" p={profit.data?.month} onClick={() => c && setRange(c.month.from, c.month.to)} active={Boolean(c && rangeActive(c.month.from, c.month.to))} />
            <ProfitKpi label="רווחיות חודש שעבר" p={profit.data?.prevMonth} onClick={() => c && setRange(c.prevMonth.from, c.prevMonth.to)} active={Boolean(c && rangeActive(c.prevMonth.from, c.prevMonth.to))} />
            <ProfitKpi label="רווחיות השנה" p={profit.data?.year} onClick={() => c && setRange(c.year.from, c.year.to)} active={Boolean(c && rangeActive(c.year.from, c.year.to))} />
            {profit.data?.filtered ? <ProfitKpi label="רווחיות בטווח שנבחר" p={profit.data.filtered} /> : null}
          </section>
        ) : null}

        {/* Search & range */}
        <section className="panel">
          <div className="panel-body row wrap" style={{ gap: 10 }}>
            <div className="search-input" style={{ flex: '1 1 320px' }}>
              <IconSearch />
              <Input
                placeholder="חיפוש לפי מספר חשבונית, שם לקוח, ח.פ או תיאור / פריט…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                aria-label="חיפוש חשבוניות"
                autoFocus
              />
            </div>
            <label className="row small muted">
              מתאריך
              <Input type="date" value={from} onChange={(e) => update({ from: e.target.value || null })} style={{ width: 150 }} />
            </label>
            <label className="row small muted">
              עד
              <Input type="date" value={to} onChange={(e) => update({ to: e.target.value || null })} style={{ width: 150 }} />
            </label>
            {from || to || q ? (
              <button
                className="btn ghost sm"
                onClick={() => {
                  setQ('');
                  update({ from: null, to: null, q: null });
                }}
              >
                <IconX size={14} /> ניקוי
              </button>
            ) : null}
          </div>
          {sales.data ? (
            <div className="panel-foot row wrap small" style={{ gap: 18 }}>
              <span className="muted">בתצוגה הנוכחית:</span>
              <span>
                <strong className="tabular">{sales.data.filteredTotals.invoices}</strong> חשבוניות
                {sales.data.filteredTotals.cancelled ? <> · {sales.data.filteredTotals.cancelled} בוטלו בזיכוי</> : null}
                {sales.data.filteredTotals.credits ? <> · {sales.data.filteredTotals.credits} זיכויים ללא חשבונית</> : null}
              </span>
              <span>לפני מע״מ <strong className="tabular">{fmtMoney(sales.data.filteredTotals.subtotal)}</strong></span>
              <span>מע״מ <span className="tabular">{fmtMoney(sales.data.filteredTotals.vat)}</span></span>
              <span>כולל מע״מ <strong className="tabular">{fmtMoney(sales.data.filteredTotals.total)}</strong></span>
              {showProfit && profit.data ? (
                <span title={profitTitle(profit.data.filtered ?? profit.data.all)}>
                  רווחיות <MarginBadge margin={(profit.data.filtered ?? profit.data.all).margin} level={(profit.data.filtered ?? profit.data.all).level} />
                  {q ? <span className="faint"> (לא לפי החיפוש)</span> : null}
                </span>
              ) : null}
            </div>
          ) : null}
        </section>

        <div className="grid-main-side wide-side">
          {/* Invoice list */}
          <section className="panel">
            <div className="panel-head">
              <h2>חשבוניות</h2>
              <span className="spacer" />
              {docs.data ? <span className="small muted tabular">{docs.data.total} {t.common.results}</span> : null}
            </div>
            {docs.error ? (
              <ErrorState error={docs.error} onRetry={() => docs.refetch()} />
            ) : (
              <>
                <DocTable
                  selectable
                  docs={docs.data?.items}
                  loading={docs.isLoading}
                  columns={['type', 'number', 'date', 'customer', 'subtotal', 'margin', 'total', 'payment', 'due']}
                  sort={sort}
                  dir={dir}
                  onSort={(k) => update({ sort: k, dir: sort === k && dir === 'desc' ? 'asc' : 'desc' })}
                  empty={<Empty title="לא נמצאו חשבוניות" body={q || from || to ? 'נסו חיפוש אחר או טווח תאריכים רחב יותר.' : 'חשבוניות שמופקות במורנינג ייכנסו לכאן אוטומטית.'} />}
                />
                {docs.data && docs.data.total > docs.data.pageSize ? (
                  <Pagination page={docs.data.page} pageSize={docs.data.pageSize} total={docs.data.total} onPage={(p) => update({ page: String(p) })} />
                ) : null}
              </>
            )}
          </section>

          {/* Breakdown by week / month */}
          <section className="panel">
            <div className="panel-head">
              <h2>מכירות לפי {group === 'week' ? 'שבוע' : 'חודש'}</h2>
              <span className="spacer" />
              <div className="segmented" role="group" aria-label="קיבוץ">
                <button className={group === 'week' ? 'active' : ''} onClick={() => update({ group: 'week' })}>שבועות</button>
                <button className={group === 'month' ? 'active' : ''} onClick={() => update({ group: 'month' })}>חודשים</button>
              </div>
            </div>
            <div className="table-wrap" style={{ maxHeight: 640, overflowY: 'auto' }}>
              <table className="table">
                <thead>
                  <tr>
                    <th>{group === 'week' ? 'שבוע (א׳–ש׳)' : 'חודש'}</th>
                    <th className="num">חשבוניות</th>
                    <th className="num">לפני מע״מ</th>
                    {showProfit && group === 'month' ? <th className="num">רווחיות</th> : null}
                    <th className="num hide-sm">כולל מע״מ</th>
                  </tr>
                </thead>
                <tbody>
                  {sales.isLoading ? (
                    <SkeletonRows cols={showProfit && group === 'month' ? 5 : 4} rows={8} />
                  ) : sales.data && sales.data.periods.length === 0 ? (
                    <tr>
                      <td colSpan={showProfit && group === 'month' ? 5 : 4} style={{ height: 'auto' }}>
                        <Empty title="אין מכירות בטווח" />
                      </td>
                    </tr>
                  ) : (
                    sales.data?.periods.map((p) => {
                      const selected = rangeActive(p.start, p.end);
                      return (
                        <tr
                          key={p.start}
                          className={`clickable ${selected ? 'selected' : ''}`}
                          onClick={() => (selected ? setRange(null, null) : setRange(p.start, p.end))}
                          title="לחיצה מציגה את החשבוניות של התקופה"
                        >
                          <td>
                            <div className="nowrap">{periodLabel(p)}</div>
                            <div className="bar-track" style={{ marginTop: 4, width: `${Math.max(3, (Math.abs(p.subtotal ?? 0) / maxPeriod) * 100)}%` }}>
                              <div className="paid" style={{ width: '100%' }} />
                            </div>
                          </td>
                          <td className="num">
                            {p.invoices}
                            {p.cancelled ? <span className="sub">{p.cancelled} בוטלו</span> : null}
                            {p.credits ? <span className="sub">{p.credits} זיכויים</span> : null}
                          </td>
                          <td className="num strong">{fmtMoney(p.subtotal)}</td>
                          {showProfit && group === 'month' ? (
                            <td className="num">
                              {(() => {
                                const m = profitByMonth.get(p.start.slice(0, 7));
                                return m ? (
                                  <span title={profitTitle(m)}>
                                    <MarginBadge margin={m.margin} level={m.level} title={profitTitle(m)} />
                                    {m.margin != null ? <span className="sub">{fmtMoney(m.profit, { round: true })}</span> : null}
                                  </span>
                                ) : <span className="faint">—</span>;
                              })()}
                            </td>
                          ) : null}
                          <td className="num hide-sm">{fmtMoney(p.total)}</td>
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </section>
        </div>
      </div>
    </>
  );
}

function SalesKpi({ label, t: v, compare, loading, onClick, active }: { label: string; t?: Totals; compare?: Totals; loading?: boolean; onClick?: () => void; active?: boolean }) {
  return (
    <button
      onClick={onClick}
      className={`kpi kpi-button ${active ? 'active' : ''}`}
      aria-pressed={active}
    >
      <div className="kpi-label">
        {label}
        {compare ? <Delta now={v?.subtotal ?? null} before={compare.subtotal} /> : null}
      </div>
      <div className="kpi-value">{loading ? <span className="skeleton" style={{ width: 90, height: 20, marginTop: 5 }} /> : fmtMoney(v?.subtotal ?? 0, { round: true })}</div>
      <div className="kpi-meta">
        {v ? (
          <>
            {v.invoices} חשבוניות · כולל מע״מ {fmtMoney(v.total, { round: true })}
          </>
        ) : null}
      </div>
    </button>
  );
}
